import * as React from "react";
import {useCallback} from "react";
import {OrderDocumentDTO, OrderDocumentsDTO, OrderDocumentType} from "@/api/data-contracts";
import {ListItem, ListItemButton, ListItemIcon} from "@mui/material";
import List from "@mui/material/List";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {FileUploadButtonEx} from "@/lib/components/buttons/FileUploadButton";
import {FileDropArea} from "@/lib/components/FileDropArea";
import {useFileUploadHandler} from "@/lib/file-upload-handler";
import {orderDataDocumentApi} from "@/boxhub-api";
import {GroupBox} from "@/lib/components/GroupBox";
import Typography from "@mui/material/Typography";
import {useSnackbarEx} from "@/lib/snackbarex";
import {CommonI18N, OrderI18N} from "@/translations";
import {useTranslation} from "react-i18next";
import {Constants} from "@/api/constants";
import {DeleteOutlined} from "@mui/icons-material";
import {useTheme} from "@mui/material/styles";
import {ImgIcon, PdfIcon} from "@/agentur/AgenturIcons";

export type OrderDocumentsProps = {
    title: string;
    orderDocuments?: OrderDocumentsDTO;
    type: OrderDocumentType;
    onFileRemoved?: (file: OrderDocumentDTO) => void;
    onFileAdded?: (file: OrderDocumentDTO[]) => void;
    disabled?: boolean;
    error?: string;
};

export const OrderDocuments = ({
    title,
    orderDocuments,
    type,
    onFileRemoved,
    onFileAdded,
    disabled,
    error
}: OrderDocumentsProps) => {
    const { t } = useTranslation();

    const theme = useTheme();

    const { showMessage } = useSnackbarEx();

    const filtered = orderDocuments?.documents?.filter((file) => file.type === type);

    const readonly = disabled || orderDocuments?.id == null;

    const uploadFunction = useCallback(
        async (files: File[]) =>
            orderDataDocumentApi
                .uploadOrderDocument(
                    {
                        orderDocumentsId: orderDocuments?.id!,
                        type
                    },
                    {
                        files
                    }
                )
                .then((res) => {
                    if (onFileAdded) {
                        onFileAdded(res.data);
                    }
                    return res;
                })
                .catch((error) => {
                    if (error.status === 413) {
                        showMessage({
                            summary: t(CommonI18N.message.uploadLimit, {maxSize: Constants.DOCUMENTS_SIZE_MAX}),
                            severity: "error"
                        });
                    } else {
                        showMessage({summary: t(CommonI18N.message.contentTooLargeSummary), severity: "error", error});
                    }
                }),
        [onFileAdded, orderDocuments?.id, showMessage, t, type]
    );

    const uploader = useFileUploadHandler(uploadFunction);

    const handleDelete = (file: OrderDocumentDTO) => async () => {
        if (file.id) {
            try {
                await orderDataDocumentApi.deleteOrderDocument(file.id);
                if (onFileRemoved) {
                    onFileRemoved(file);
                }
            } catch (error) {
                showMessage({summary: t(OrderI18N.details.message.documentDeleteFailed), severity: "error", error});
            }
        }
    };

    const handleDownload = (file: OrderDocumentDTO) => {
        const url = `/api/order-documents/${file.id!}/download/${encodeURIComponent(file.filenName!)}`;
        window.open(url, "_blank");
    };

    return (
        <GroupBox title={title} disabled={disabled}>
            <FileDropArea uploader={uploader} disabled={readonly}>
                <List dense>
                    {filtered?.map((file) => (
                        <ListItem
                            key={file.id}
                            sx={{ padding: 0, width: "100%", "&>div": { padding: 1 } }}
                            secondaryAction={
                                readonly ? undefined : (
                                    <IconButtonEx Icon={DeleteOutlined} size="small" onClick={handleDelete(file)} />
                                )
                            }
                        >
                            <ListItemButton sx={{ padding: 2, width: "100%" }} onClick={() => handleDownload(file)}>
                                <ListItemIcon>
                                    {file.filenName && /.+\.pdf/i.test(file.filenName) ? (
                                        <PdfIcon mode={theme.palette.mode} />
                                    ) : (
                                        <ImgIcon mode={theme.palette.mode} />
                                    )}
                                </ListItemIcon>
                                {file.filenName}
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {!readonly && filtered?.length! < 5 && (
                        <ListItem style={{ display: "flex", justifyContent: "center" }}>
                            <FileUploadButtonEx
                                uploader={uploader}
                                label={t(CommonI18N.action.uploadImageOrPdf)}
                                size="small"
                                variant="text"
                                accept="application/pdf,image/jpeg,image/png,image/tiff,image/bmp"
                                multiple
                                fullWidth
                            />
                        </ListItem>
                    )}
                    {orderDocuments?.id == null && !disabled && (
                        <ListItem style={{ display: "flex", justifyContent: "center" }}>
                            <Typography
                                    variant="subtitle1">{t(OrderI18N.details.message.documentSaveFirst)}</Typography>
                        </ListItem>
                    )}
                    {(orderDocuments?.id != null || !!disabled) && !filtered?.length && (
                        <ListItem style={{ display: "flex", justifyContent: "center" }}>
                            <Typography variant="subtitle1">{t(OrderI18N.details.message.noDocument)}</Typography>
                            {error && (
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{
                                        mt: 0.5,
                                        fontSize: "0.875rem",
                                        textAlign: "center",
                                        width: "100%"
                                    }}
                                >
                                    {error}
                                </Typography>
                            )}
                        </ListItem>
                    )}
                    {orderDocuments?.id != null && filtered?.length! > 0 && error && (
                        <ListItem style={{ display: "flex", justifyContent: "center" }}>
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{
                                    fontSize: "0.875rem",
                                    textAlign: "center",
                                    width: "100%"
                                }}
                            >
                                {error}
                            </Typography>
                        </ListItem>
                    )}
                </List>
            </FileDropArea>
        </GroupBox>
    );
};
